/**
 * "Coffee bean to the bag" micro-interaction, played only after Shopify has confirmed an
 * add-to-cart. Plain DOM + Web Animations API: no library, no image request, nothing loaded
 * until it runs. The bean is a fixed-position element, so it never causes horizontal overflow.
 */

const SIZE = 16; // px, bean width

// Dark roast bean with its centre crease and a thin copper highlight
const beanSvg = (id: string) => `
<svg viewBox="0 0 20 26" width="${SIZE}" height="${Math.round(SIZE * 1.3)}" aria-hidden="true" focusable="false">
  <defs>
    <linearGradient id="${id}" x1="0.15" y1="0" x2="0.85" y2="1">
      <stop offset="0" stop-color="#5b3722"/>
      <stop offset="1" stop-color="#23130a"/>
    </linearGradient>
  </defs>
  <ellipse cx="10" cy="13" rx="8.4" ry="11.6" fill="url(#${id})"/>
  <path d="M10.2 2.4c-2.8 3.5-2.8 7.1 0 10.6s2.8 7.1 0 10.6" fill="none" stroke="#130a04" stroke-width="1.7" stroke-linecap="round"/>
  <path d="M4.9 7.2c.8-2 2.1-3.4 3.6-4.1" fill="none" stroke="#d09762" stroke-width="1.1" stroke-linecap="round" opacity=".8"/>
</svg>`;

// Soft, premium ease: quick lift-off, long gentle landing
const EASE = 'cubic-bezier(0.45, 0.05, 0.2, 1)';

function createBean(): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = beanSvg(`nomafe-bean-${Date.now()}`);
  Object.assign(el.style, {
    position: 'fixed',
    left: '0px',
    top: '0px',
    zIndex: '70',
    pointerEvents: 'none',
    willChange: 'transform, opacity',
    filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.45))',
  });
  document.body.appendChild(el);
  return el;
}

const isOnScreen = (r: DOMRect) =>
  r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Bean rises a few pixels next to the button and fades out (fallback when the bag is not usable as a target). */
function riseAndFade(start: { x: number; y: number }): Promise<void> {
  const el = createBean();
  const at = (dy: number, rot: number, s: number) =>
    `translate(${start.x - SIZE / 2}px, ${start.y - SIZE * 0.65 + dy}px) rotate(${rot}deg) scale(${s})`;
  const anim = el.animate(
    [
      { transform: at(6, -12, 0.7), opacity: 0 },
      { transform: at(-4, 0, 1), opacity: 1, offset: 0.35 },
      { transform: at(-18, 16, 0.92), opacity: 0 },
    ],
    { duration: 560, easing: EASE, fill: 'forwards' }
  );
  return anim.finished.then(() => el.remove(), () => el.remove());
}

/**
 * Flies a bean from `from` (the add-to-cart button) to `to` (the header bag) along a curved
 * path. Resolves when the bean has landed, so the caller can update the cart count right then.
 */
export function flyBean(from: Element, to: Element | null): Promise<void> {
  if (typeof window === 'undefined' || typeof document.body.animate !== 'function') return Promise.resolve();

  const a = from.getBoundingClientRect();
  // Appears just above the button, a little right of its centre
  const start = { x: a.left + a.width * 0.58, y: a.top + 2 };
  if (!isOnScreen(a)) return Promise.resolve();

  const b = to?.getBoundingClientRect();
  if (!b || !isOnScreen(b)) return riseAndFade(start);

  const end = { x: b.left + b.width / 2, y: b.top + b.height / 2 };
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dist = Math.hypot(dx, dy);
  // Target too close, or not above the button: the arc would look odd
  if (dist < 70 || dy > -30) return riseAndFade(start);

  const small = window.innerWidth < 768;
  const duration = small ? clamp(520 + dist * 0.12, 520, 660) : clamp(650 + dist * 0.14, 650, 850);

  // Quadratic Bézier: the control point lifts the path into a gentle arc that leans towards the bag
  const lift = clamp(dist * 0.28, small ? 30 : 50, small ? 110 : 170);
  const ctrl = {
    x: clamp(start.x + dx * 0.3, 8, window.innerWidth - 8),
    y: Math.max(12, Math.min(start.y, end.y) - lift),
  };
  const spin = dx >= 0 ? 300 : -300;

  const frames: Keyframe[] = [];
  const steps = 28;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    const x = u * u * start.x + 2 * u * t * ctrl.x + t * t * end.x;
    const y = u * u * start.y + 2 * u * t * ctrl.y + t * t * end.y;
    // Grows slightly as it lifts off, then shrinks into the bag
    const scale = t < 0.4 ? 0.75 + (t / 0.4) * 0.4 : 1.15 - ((t - 0.4) / 0.6) * 0.55;
    const opacity = t < 0.08 ? t / 0.08 : t > 0.86 ? Math.max(0, (1 - t) / 0.14) : 1;
    frames.push({
      offset: t,
      opacity,
      transform: `translate(${x - SIZE / 2}px, ${y - SIZE * 0.65}px) rotate(${spin * t - 15}deg) scale(${scale})`,
    });
  }

  const el = createBean();
  const anim = el.animate(frames, { duration, easing: EASE, fill: 'forwards' });
  return anim.finished.then(() => el.remove(), () => el.remove());
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
